/**
 * Hermes Button - Enhanced button with Framer Motion animations
 *
 * Features:
 * - Spring-physics hover/tap animations
 * - Module color variants (courier, santa, email)
 * - Loading state with spinner
 * - Accessibility support with reduced-motion preference
 */

import React from 'react';
import { Button as MuiButton, ButtonProps as MuiButtonProps, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Module color variant type
export type ModuleVariant = 'courier' | 'santa' | 'email' | 'primary' | 'secondary';

export interface HermesButtonProps extends Omit<MuiButtonProps, 'color'> {
  /**
   * Module color variant
   */
  moduleVariant?: ModuleVariant;

  /**
   * Loading state - shows spinner and disables button
   */
  loading?: boolean;

  /**
   * Icon to display before text
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to display after text
   */
  endIcon?: React.ReactNode;
}

// Map module variants to theme palette keys
const variantColorMap: Record<ModuleVariant, string> = {
  courier: 'courier',
  santa: 'santa',
  email: 'email',
  primary: 'primary',
  secondary: 'secondary',
};

// Styled button with dynamic module colors
const StyledButton = styled(MuiButton, {
  shouldForwardProp: (prop) => prop !== 'moduleVariant',
})<{ moduleVariant?: ModuleVariant }>(({ theme, moduleVariant, variant }) => {
  const colorKey = moduleVariant ? variantColorMap[moduleVariant] : 'primary';
  const color = theme.palette[colorKey as keyof typeof theme.palette] as any;

  return {
    position: 'relative',
    overflow: 'hidden',
    fontWeight: 600,
    letterSpacing: '0.01em',
    transition: 'none', // Disable default transitions (using Framer Motion)

    // Contained variant colors
    ...(variant === 'contained' && {
      backgroundColor: color.main,
      color: color.contrastText || '#FFFFFF',
      '&:hover': {
        backgroundColor: color.dark || color.main,
      },
      '&:active': {
        backgroundColor: color.dark || color.main,
      },
    }),

    // Outlined variant colors
    ...(variant === 'outlined' && {
      borderColor: color.main,
      color: color.main,
      borderWidth: 2,
      '&:hover': {
        borderWidth: 2,
        borderColor: color.dark || color.main,
        backgroundColor: `${color.main}10`,
      },
    }),

    // Text variant colors
    ...(variant === 'text' && {
      color: color.main,
      '&:hover': {
        backgroundColor: `${color.main}10`,
      },
    }),

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },

    // Loading state overlay
    '&.loading': {
      pointerEvents: 'none',
      color: 'transparent',
    },
  };
});

// Loading spinner container
const LoadingSpinner = styled(CircularProgress)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  marginTop: -12,
  marginLeft: -12,
  color: 'inherit',
}));

// Animation variants for Framer Motion
const motionVariants = {
  rest: {
    scale: 1,
  },
  hover: {
    scale: 1.02,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
  tap: {
    scale: 0.98,
    transition: {
      type: 'spring' as const,
      stiffness: 400,
      damping: 17,
    },
  },
};

export const Button = React.forwardRef<HTMLButtonElement, HermesButtonProps>(
  (
    {
      moduleVariant = 'primary',
      loading = false,
      children,
      disabled,
      startIcon,
      endIcon,
      variant = 'contained',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    // Check for reduced motion preference
    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return (
      <motion.div
        initial="rest"
        whileHover={!isDisabled && !prefersReducedMotion ? 'hover' : 'rest'}
        whileTap={!isDisabled && !prefersReducedMotion ? 'tap' : 'rest'}
        variants={prefersReducedMotion ? {} : motionVariants}
        style={{ display: 'inline-block' }}
      >
        <StyledButton
          ref={ref}
          moduleVariant={moduleVariant}
          disabled={isDisabled}
          variant={variant}
          className={loading ? 'loading' : undefined}
          startIcon={!loading && startIcon}
          endIcon={!loading && endIcon}
          {...props}
        >
          {loading && <LoadingSpinner size={24} />}
          {children}
        </StyledButton>
      </motion.div>
    );
  }
);

Button.displayName = 'HermesButton';

export default Button;
